import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts'
import { reportService } from '@/services/reportService'

interface Period {
  id: string
  start: Date | null
  end: Date | null
  label?: string
}

interface PeriodData {
  deposits: number
  withdrawals: number
  net: number
  transactionCount?: number
  successRate?: number
}

interface MultiPeriodComparison {
  [periodId: string]: PeriodData
}

const MultiPeriodComparator = () => {
  const [periods, setPeriods] = useState<Period[]>([
    {
      id: '1',
      start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      end: new Date(),
      label: 'Period 1',
    },
    {
      id: '2',
      start: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
      end: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      label: 'Period 2',
    },
  ])
  const [comparison, setComparison] = useState<MultiPeriodComparison>({})
  const [loading, setLoading] = useState(false)

  const loadComparison = useCallback(async () => {
    const validPeriods = periods.filter((p) => p.start && p.end)
    if (validPeriods.length < 2) return

    setLoading(true)
    try {
      const comparisons: MultiPeriodComparison = {}

      // Load data for each period
      for (const period of validPeriods) {
        try {
          const params = {
            left_period_start: period.start!.toISOString(),
            left_period_end: period.end!.toISOString(),
            right_period_start: period.start!.toISOString(),
            right_period_end: period.end!.toISOString(),
          }
          const data = await reportService.comparePeriods(params)
          comparisons[period.id] = data.left
        } catch (error) {
          console.error(`Failed to load period ${period.id}`, error)
          comparisons[period.id] = { deposits: 0, withdrawals: 0, net: 0 }
        }
      }

      setComparison(comparisons)
    } catch (error) {
      console.error('Failed to load comparison', error)
    } finally {
      setLoading(false)
    }
  }, [periods])

  useEffect(() => {
    loadComparison()
  }, [loadComparison])

  const addPeriod = () => {
    if (periods.length >= 5) return
    const newId = String(periods.length + 1)
    setPeriods([
      ...periods,
      {
        id: newId,
        start: new Date(Date.now() - (periods.length + 1) * 7 * 24 * 60 * 60 * 1000),
        end: new Date(Date.now() - periods.length * 7 * 24 * 60 * 60 * 1000),
        label: `Period ${newId}`,
      },
    ])
  }

  const removePeriod = (id: string) => {
    if (periods.length <= 2) return
    setPeriods(periods.filter((p) => p.id !== id))
  }

  const updatePeriod = (id: string, field: 'start' | 'end' | 'label', value: Date | null | string) => {
    setPeriods(periods.map((p) => (p.id === id ? { ...p, [field]: value } : p)))
  }

  const chartData = useMemo(() => {
    return periods
      .filter((p) => comparison[p.id])
      .map((period) => ({
        period: period.label || `Period ${period.id}`,
        deposits: comparison[period.id]?.deposits || 0,
        withdrawals: comparison[period.id]?.withdrawals || 0,
        net: comparison[period.id]?.net || 0,
      }))
  }, [periods, comparison])

  const tableData = useMemo(() => {
    return periods
      .filter((p) => comparison[p.id])
      .map((period) => {
        const data = comparison[period.id]
        return {
          period: period.label || `Period ${period.id}`,
          deposits: data.deposits,
          withdrawals: data.withdrawals,
          net: data.net,
          depositChange: periods.indexOf(period) > 0
            ? ((data.deposits - comparison[periods[0].id].deposits) / comparison[periods[0].id].deposits) * 100
            : 0,
          withdrawalChange: periods.indexOf(period) > 0
            ? ((data.withdrawals - comparison[periods[0].id].withdrawals) / comparison[periods[0].id].withdrawals) * 100
            : 0,
          netChange: periods.indexOf(period) > 0
            ? ((data.net - comparison[periods[0].id].net) / Math.abs(comparison[periods[0].id].net || 1)) * 100
            : 0,
        }
      })
  }, [periods, comparison])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Multi-Period Comparison</Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addPeriod}
          disabled={periods.length >= 5}
        >
          Add Period
        </Button>
      </Stack>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            {periods.map((period, index) => (
              <Grid item xs={12} md={6} lg={periods.length <= 2 ? 6 : 4} key={period.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">{period.label || `Period ${period.id}`}</Typography>
                      {periods.length > 2 && (
                        <IconButton size="small" onClick={() => removePeriod(period.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <Stack spacing={2}>
                      <DatePicker
                        label="Start Date"
                        value={period.start}
                        onChange={(date) => updatePeriod(period.id, 'start', date)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                      <DatePicker
                        label="End Date"
                        value={period.end}
                        onChange={(date) => updatePeriod(period.id, 'end', date)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </LocalizationProvider>

      {loading && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography>Loading comparison data...</Typography>
        </Paper>
      )}

      {!loading && Object.keys(comparison).length > 0 && (
        <>
          {/* Comparison Chart */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Visual Comparison
            </Typography>
            <Box sx={{ height: 400, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="deposits" fill="#2e7d32" name="Deposits" />
                  <Bar dataKey="withdrawals" fill="#c62828" name="Withdrawals" />
                  <Bar dataKey="net" fill="#1976d2" name="Net Flow" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Comparison Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Comparison
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Deposits</TableCell>
                    <TableCell align="right">Withdrawals</TableCell>
                    <TableCell align="right">Net Flow</TableCell>
                    <TableCell align="right">Deposit Change %</TableCell>
                    <TableCell align="right">Withdrawal Change %</TableCell>
                    <TableCell align="right">Net Change %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip label={row.period} size="small" color={index === 0 ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(row.deposits)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.withdrawals)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={row.net >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="medium"
                        >
                          {formatCurrency(row.net)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {index > 0 && (
                          <Chip
                            label={`${row.depositChange >= 0 ? '+' : ''}${row.depositChange.toFixed(1)}%`}
                            size="small"
                            color={row.depositChange >= 0 ? 'success' : 'error'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {index > 0 && (
                          <Chip
                            label={`${row.withdrawalChange >= 0 ? '+' : ''}${row.withdrawalChange.toFixed(1)}%`}
                            size="small"
                            color={row.withdrawalChange >= 0 ? 'success' : 'error'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {index > 0 && (
                          <Chip
                            label={`${row.netChange >= 0 ? '+' : ''}${row.netChange.toFixed(1)}%`}
                            size="small"
                            color={row.netChange >= 0 ? 'success' : 'error'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Summary Cards */}
          <Grid container spacing={3}>
            {periods
              .filter((p) => comparison[p.id])
              .map((period) => {
                const data = comparison[period.id]
                return (
                  <Grid item xs={12} sm={6} md={4} lg={periods.length <= 3 ? 4 : 3} key={period.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {period.label || `Period ${period.id}`}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Deposits
                            </Typography>
                            <Typography variant="h6">{formatCurrency(data.deposits)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Withdrawals
                            </Typography>
                            <Typography variant="h6">{formatCurrency(data.withdrawals)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Net Flow
                            </Typography>
                            <Typography
                              variant="h6"
                              color={data.net >= 0 ? 'success.main' : 'error.main'}
                            >
                              {formatCurrency(data.net)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
          </Grid>
        </>
      )}
    </Box>
  )
}

export default MultiPeriodComparator


